/**
 * useContentStrategyTask — wraps useBackendTask for a content-plan pipeline.
 *
 * Pattern mirrors useInsightsGenerationTask (insights-analyzer):
 *   - subscribes to projects/{pid}/backend_tasks/{taskId}
 *   - exposes phase progress derived from task.phases + currentPhaseKey
 *   - resolves cancellation hook via BackendTaskService
 */

import { useMemo } from 'react';
import { useBackendTask } from '@modules/content-agent/hooks/useBackendTask';

export interface ContentStrategyTaskStatus {
  /** The raw task document */
  task: unknown | null;
  /** The machine-readable current phase key */
  currentPhase?: string;
  /** Display name of the current phase */
  currentPhaseName?: string;
  /** Lifecycle: running | completed | error */
  status?: 'running' | 'completed' | 'error' | 'pending';
  /** Error message if the task terminated with error */
  error?: string;
  /** Percentage 0-100 — derived from phase index / total phases */
  progressPercent?: number;
  /** Token usage accumulated so far */
  accumulatedUsage?: { inputTokens: number; outputTokens: number };
  /** Accumulated cost (USD) */
  accumulatedCostUsd?: number;
  /** Strategy id resolved on pipeline init — for redirect on completion */
  strategyId?: string;
  /** Post/sprint counts from the terminal result */
  postCount?: number;
  sprintCount?: number;
  loading: boolean;
  refresh: () => void;
}

export function useContentStrategyTask(
  projectId: string | null,
  taskId: string | null,
): ContentStrategyTaskStatus {
  const { task, loading, refresh } = useBackendTask(projectId, taskId);

  return useMemo(() => {
    if (!task) return { task: null, loading, refresh };

    const t = task as any;
    const phases = t.phases ?? {};
    const currentPhaseKey = t.currentPhaseKey as string | undefined;
    const phaseKeys = Object.keys(phases);
    const currentIndex = currentPhaseKey ? phaseKeys.indexOf(currentPhaseKey) : -1;
    const progressPercent = phaseKeys.length > 0 && currentIndex >= 0
      ? Math.round(((currentIndex + 1) / phaseKeys.length) * 100)
      : 0;

    return {
      task,
      currentPhase: currentPhaseKey,
      currentPhaseName: currentPhaseKey ? phases[currentPhaseKey]?.displayName : undefined,
      status: t.status,
      error: t.error,
      progressPercent,
      accumulatedUsage: t.accumulatedUsage,
      accumulatedCostUsd: t.accumulatedCostUsd,
      strategyId: t.strategyId ?? t.result?.strategyId,
      postCount: t.result?.postCount,
      sprintCount: t.result?.sprintCount,
      loading,
      refresh,
    };
  }, [task, loading, refresh]);
}
